import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { EQUAL, Filter, IS_EMPTY, IS_NOT_EMPTY, Operator, OR } from "@dto/records";
import User from "@dto/Users";
import { getOperatorLabel } from "@utils/model";
import { TW_FilterEditorOperatorSelect, TW_FilterEditorOperatorSelectFull, TW_FilterEditorValue_Wrapper } from "@utils/twClasses";
import { USER_FORMULA } from "@utils/users";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "src/store";
import { UserSelect } from "./UserSelect";

interface UserFilterValueSelectorProps {
  selectedPathKey: string | undefined;
  filter: Filter;
  validOperators: Operator[];
  validateFilter: (filter: Filter) => void;
  disabled: boolean
}

export function UserFilterValueSelector({ selectedPathKey, filter, validOperators, validateFilter, disabled }: UserFilterValueSelectorProps) {

  const { t } = useTranslation();
  const [selectedOperator, setSelectedOperator] = useState<Operator | undefined>(EQUAL);
  const [selectedUserKeys, setSelectedUserKeys] = useState<string[] | undefined>(undefined);
  const [currentUserSelected, setCurrentUserSelected] = useState<boolean>(false);
  const users = useAppStore((state) => state.usersByKeys);

  useEffect(() => {
    if (filter) {
      const fieldFilter = filter.groupFilter;
      const filters = fieldFilter?.filters;
      if (filters) {
        const firstFilter = filters[0];
        const valueKeys = filters.map(filter => filter.singleFilter?.value).filter(value => value !== null) as string[];
        setSelectedOperator(firstFilter.singleFilter?.operator);
        setSelectedUserKeys(valueKeys);
        setCurrentUserSelected(filters.some(filter => filter.singleFilter?.formula?.trim() === USER_FORMULA));
      }
    }
  }, [filter]);

  const onOperatorChange = (operator: Operator) => {
    setSelectedOperator(operator);

    if (selectedPathKey && operator && (selectedUserKeys || operator === IS_EMPTY || operator === IS_NOT_EMPTY)) {
      onFilterChange(selectedPathKey, operator, selectedUserKeys || [], currentUserSelected);
    }
  }

  const onValueChange = (users: User[], currentUserSelected: boolean) => {
    const newSelectedUserKeys = users.map(user => user._key);
    setSelectedUserKeys(newSelectedUserKeys);
    setCurrentUserSelected(currentUserSelected);
    if (selectedPathKey && selectedOperator) {
      onFilterChange(selectedPathKey, selectedOperator, newSelectedUserKeys, currentUserSelected);
    }
  }

  function onFilterChange(pathKey: string, operator: Operator, userKeys: string[], currentUserSelected: boolean) {
    if (!pathKey || !operator || (!userKeys && operator !== IS_EMPTY && operator !== IS_NOT_EMPTY)) {
      return;
    }

    const newFieldFilters: Filter[] = [];
    if (operator === IS_EMPTY || operator === IS_NOT_EMPTY) {
      newFieldFilters.push({
        singleFilter: {
          pathKey: pathKey,
          operator: operator,
          value: null,
          valueKey: null,
          formula: null
        },
        groupFilter: null
      });
    } else {
      userKeys.forEach(userKey => {
        newFieldFilters.push({
          singleFilter: {
            pathKey: pathKey,
            operator: operator,
            value: userKey,
            valueKey: null,
            formula: null
          },
          groupFilter: null
        });
      })
      if (currentUserSelected) {
        newFieldFilters.push({
          singleFilter: {
            pathKey: pathKey,
            operator: operator,
            value: null,
            valueKey: null,
            formula: USER_FORMULA
          },
          groupFilter: null
        });
      }
    }

    const fieldFilter: Filter = {
      singleFilter: null,
      groupFilter: {
        filters: newFieldFilters,
        combinator: OR
      }
    }

    validateFilter(fieldFilter);
  }

  const allUsers = Array.from(users.values());
  const selectedUsers = selectedUserKeys?.map(key => users.get(key)).filter(user => user !== undefined);

  const disabledOperator = false;
  return (
    <div className={TW_FilterEditorValue_Wrapper}>
      <Select onValueChange={onOperatorChange} value={selectedOperator} disabled={disabled}>
        <SelectTrigger className={(selectedOperator === IS_EMPTY || selectedOperator === IS_NOT_EMPTY) ? TW_FilterEditorOperatorSelectFull : TW_FilterEditorOperatorSelect} disabled={disabledOperator}>
          <SelectValue placeholder={t("filter.operator.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {validOperators.map((operator) => {
            return (
              <SelectItem key={operator} value={operator}>{getOperatorLabel(operator, t)}</SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      {selectedOperator !== IS_EMPTY && selectedOperator !== IS_NOT_EMPTY &&
        <UserSelect allUsers={allUsers || []} selectedUsers={selectedUsers?.filter(user => user !== undefined) as User[] || []} disabled={disabledOperator || disabled} currentUserSelected={currentUserSelected} onSelectionChange={onValueChange} />
      }
    </div>
  )
}